<template>
  <component v-if="!isDefaultTemplate" :is='templateComponent'
    :field="field"
    :expand-field="expandField"
    :expanded="expanded"
    :row-type="rowType"
    :level="level"
    :column-index="columnIndex"
    :columns-count="columnsCount"
    :format="format"
    :data-item="dataItem"
    :class-name="className"
    :type="type"
    :edit-field="editField"
    :cur-edit-field="curEditField"
    :extraparams="extraparams"
    @blur="onBlur"
    @click="onClick"
    @groupclick="onGroupClick"
    @change="onChange"
    @cellclick="onCellclick"
  />
  <td v-else-if="renderCell && typeof dataItem[field] === 'boolean'" :class="className">
    <base-icon-clickable v-if="showExpand" class="m-auto" :name="isExpanded ? 'chevronup' : 'chevrondown'" size="sm" @click="onRowExpandChange" />
    <input v-else-if="single" class='k-radio' type='radio' :name="finalTableId" :checked="dataItem[field]" @click="onCheckBoxClick" />
    <input v-else class='k-checkbox' type='checkbox' :name="finalTableId" :checked="dataItem[field]" @click="onCheckBoxClick" />
  </td>
  <td v-else-if="renderCell && isEditable" :class="className" @click="cellclick">
    <el-input ref="cellinput" class="w-full" v-model="localValue" size="mini" tabindex="0" @change="change" @blur="blur" />
  </td>
  <td v-else-if="renderCell" :class="className" @click="cellclick">{{ value }}</td>
  <td v-else-if="renderArrow" :key="'g' + columnIndex" :class="'k-group-cell'" />
  <td v-else-if="columnIndex <= level" key='g-colspan' :colSpan="columnsCount - columnIndex">
    <p class='k-reset'>
      <a @click="onGroupClick" tabIndex='-1' href='#' :class="expanded ? 'k-i-collapse k-icon' : 'k-i-expand k-icon'" />
      {{ groupHeader }}
    </p>
  </td>
</template>
<script>
import camelCase from 'lodash/camelCase'
import templates from '@/components/kendo/templates/_requireTemplates'
import getNestedValue from '@/services/functions/getNestedValue'
import { discard } from '@/services/functions/preserve&discard'

export default {
  name: 'common-template',
  emits: {
    click: null
  },
  props: {
    field: String,
    dataItem: Object,
    format: String,
    className: String,
    columnIndex: Number,
    columnsCount: Number,
    rowType: String,
    level: Number,
    expandField: String,
    expanded: Boolean,
    editor: String,
    type: {
      type: String,
      default: 'Text'
    },
    cell: {
      type: String,
      default: 'default',
      validator: function (value) {
        const finalValue = camelCase(value)
        return finalValue === 'default' || Object.prototype.hasOwnProperty.call(templates, finalValue)
      }
    },
    curEditField: [String, null],
    addedColumnClass: {
      type: String,
      default: 'added-column'
    },
    expandType: {
      type: [String, Boolean],
      default: 'auto'
    },
    editType: {
      type: [String, Boolean],
      default: 'row'
    },
    editField: {
      type: String,
      default: 'inEdit'
    },
    extraparams: {
      type: Object,
      default: () => ({})
    },
    single: {
      type: Boolean,
      default: false
    },
    selectOnRowClick: {
      type: Boolean,
      default: false
    },
    tableId: [String, Number]
  },
  data () {
    return {
      localValue: ''
    }
  },
  mounted () {
    this.localValue = this.value
    if (this.isEditable && this.curEditField === this.field && this.$refs.cellinput) {
      this.$refs.cellinput.focus()
    }
  },
  watch: {
    value: function (value, oldValue) {
      if (value !== oldValue) {
        this.localValue = value
      }
    }
  },
  computed: {
    finalTableId: function () {
      const prefix = this.single ? 'k-radio' : 'k-checkbox'
      return `${prefix}-${this.field}-${this.tableId}`
    },
    renderArrow () {
      var returnValue =
        this.columnIndex === undefined ||
        this.level === undefined ||
        this.columnIndex < this.level ||
        this.columnsCount === undefined ||
        this.rowType !== 'groupHeader' ||
        this.dataItem[this.field] === undefined
      // return returnValue && this.dataItem[this.field]
      return returnValue && this.dataItem[this.field] !== undefined
    },
    renderCell () {
      return this.field !== undefined && this.rowType !== 'groupHeader'
    },
    inEdit: function () {
      return getNestedValue(this.editField, this.dataItem) === true
    },
    value: function () {
      return getNestedValue(this.field, this.dataItem)
    },
    groupHeader () {
      const value = this.value
      if (this.rowType === 'groupHeader') {
        const template = this.extraparams.groupHeaderTemplate
        if (typeof template === 'function') {
          return template(this.dataItem, { value, ...discard([template.name], this.extraparams) })
        }
      }
      if (value === null || value === undefined) return '' 
      return value 
    },
    camelCell: function () {
      return camelCase(this.cell)
    },
    isDefaultTemplate: function () {
      return ['default', 'common'].includes(this.camelCell)
    },
    templateComponent: function () {
      if (this.isDefaultTemplate) {
        return null
      }
      return templates[this.camelCell]
    },
    editable: function () {
      return !!this.extraparams.editable
    },
    isCellEditable: function () {
      return this.editType === 'cell'
    },
    isEditable: function () {
      return this.editable && this.inEdit && (!this.isCellEditable || this.curEditField === this.field)
    },
    isManualExpand: function () {
      return this.expandType === 'manual'
    },
    isExpanded: function () {
      return getNestedValue(this.expandField, this.dataItem)
    },
    isExpandColumn: function () {
      return this.expandField === this.field
    },
    showExpand: function () {
      return (this.isExpandColumn && !this.isManualExpand)
    }
  },
  methods: {
    onGroupClick (e, dataItem = this.dataItem, expanded = this.expanded) {
      // @ts-ignore
      this.$emit('click', e, dataItem, expanded)
      this.$emit('expandchange', { dataItem, target: this })
    },
    onCheckBoxClick (e) {
      if (!this.selectOnRowClick) {
        this.$emit('fieldchange', { dataItem: this.dataItem, field: this.field, event: e })
      }
    },
    onRowExpandChange (e) {
      this.$emit('expandchange', { dataItem: this.dataItem, target: this })
    },
    change (value) {
      var dataItem, field
      if (typeof value === 'object' &&
        Object.hasOwnProperty.call(value, 'dataItem') &&
        Object.hasOwnProperty.call(value, 'field') &&
        Object.hasOwnProperty.call(value, 'value')) {
        ({ dataItem, field, value } = value)
      } else {
        dataItem = this.dataItem
        field = this.field
      }
      this.$emit('change', { dataItem, field, value })
    },
    blur (e) {
      this.$emit('blur', e)
    },
    cellclick (e) {
      this.$emit('cellclick', { dataItem: this.dataItem, field: this.field, event: e })
    },
    onBlur (e) {
      this.$emit('blur', e)
    },
    onClick (e) {
      this.$emit('click', e)
    },
    onChange (e) {
      this.$emit('change', e)
    },
    onCellclick (e) {
      this.$emit('cellclick', e)
    },
    getNestedValue
  },
}
</script>

<style scoped>
.bg-success-p{
  background-color: black;
}
</style>
